/**
 * Minimal typed pub/sub. The whole app is wired as
 *   pose -> recogniser -> [events] -> {AI, scoring, renderer, telemetry}
 * so that every consumer sees the *same* event stream and new consumers
 * (replay recorder, coaching overlay, drill grader) can be bolted on without
 * touching the recogniser.
 */
export type Handler<T> = (payload: T) => void;

export class Bus<Events extends object> {
  private map = new Map<keyof Events, Set<Handler<never>>>();

  on<K extends keyof Events>(type: K, fn: Handler<Events[K]>): () => void {
    let set = this.map.get(type);
    if (!set) {
      set = new Set();
      this.map.set(type, set);
    }
    set.add(fn as Handler<never>);
    return () => set!.delete(fn as Handler<never>);
  }

  emit<K extends keyof Events>(type: K, payload: Events[K]): void {
    const set = this.map.get(type);
    if (!set) return;
    for (const fn of set) (fn as Handler<Events[K]>)(payload);
  }

  clear(): void {
    this.map.clear();
  }
}
